import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { IconStore } from '../components/icons';

interface StoreApp {
  id: string;
  title: string;
  description: string | null;
  thumbnailUrl: string | null;
  projectType: 'game' | 'app' | 'tool';
  category: string;
  creator: string;
  remixCount: number;
  rating: number | null;
  ratingCount: number;
  playUrl: string | null;
  publishedAt: string;
}

const CATEGORIES = [
  { key: '', label: 'Everything' },
  { key: 'games', label: 'Games' },
  { key: 'apps', label: 'Apps' },
  { key: 'tools', label: 'Tools' },
];

/**
 * Public catalog (spec 5b.9). Browsing is open to everyone; playing and remixing are one click from
 * here, which is how most learners find their second project.
 */
export function PlaystorePage() {
  const navigate = useNavigate();
  const [apps, setApps] = useState<StoreApp[] | null>(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState<'popular' | 'new'>('popular');
  const [error, setError] = useState<string | null>(null);
  const [remixing, setRemixing] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const search = new URLSearchParams();
      if (query.trim()) search.set('q', query.trim());
      if (category) search.set('category', category);
      search.set('sort', sort);
      const data = await api.get<{ apps: StoreApp[] }>(`/box/playstore?${search.toString()}`);
      setApps(data.apps);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the playstore');
    }
  }, [query, category, sort]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 200);
    return () => window.clearTimeout(timer);
  }, [load]);

  const remix = async (app: StoreApp) => {
    setRemixing(app.id);
    try {
      const { project } = await api.post<{ project: { id: string } }>(
        `/box/projects/${app.id}/remix`,
      );
      navigate(`/projects/${project.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remix that app');
    } finally {
      setRemixing(null);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-6">
      <div className="flex items-center gap-2">
        <span className="text-accent">
          <IconStore width={20} height={20} />
        </span>
        <div>
          <h1 className="text-lg font-semibold">Playstore</h1>
          <p className="text-sm text-muted">Play what other people made — then remix it.</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search apps and games"
          className="min-w-[200px] flex-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm outline-none ring-1 ring-transparent transition focus:border-ink/50 focus:ring-ink/20"
        />
        <div className="inline-flex rounded-lg border border-line p-0.5">
          {CATEGORIES.map(c => (
            <button
              key={c.key}
              type="button"
              onClick={() => setCategory(c.key)}
              className={[
                'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                category === c.key ? 'bg-accent text-accent-ink' : 'text-muted hover:text-ink',
              ].join(' ')}
            >
              {c.label}
            </button>
          ))}
        </div>
        <select
          value={sort}
          onChange={e => setSort(e.target.value as 'popular' | 'new')}
          className="rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-muted"
          aria-label="Sort"
        >
          <option value="popular">Most remixed</option>
          <option value="new">Newest</option>
        </select>
      </div>

      {error && (
        <div className="rounded-lg border border-line bg-page p-3 text-sm text-muted">{error}</div>
      )}

      {apps === null && !error && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map(i => (
            <div key={i} className="h-36 animate-pulse rounded-xl bg-page" />
          ))}
        </div>
      )}

      {apps?.length === 0 && (
        <div className="rounded-2xl border border-dashed border-line p-10 text-center">
          <p className="text-sm text-muted">
            {query || category ? 'Nothing matches that yet.' : 'Nothing published yet — be the first.'}
          </p>
        </div>
      )}

      {apps && apps.length > 0 && (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {apps.map(app => (
            <li key={app.id} className="flex flex-col overflow-hidden rounded-2xl border border-line">
              <div className="relative grid aspect-[16/10] place-items-center overflow-hidden bg-gradient-to-br from-accent/25 via-accent/10 to-transparent">
                {app.thumbnailUrl ? (
                  <img src={app.thumbnailUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
                ) : (
                  <span className="text-3xl font-black text-accent-deep opacity-60">
                    {app.title.slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex flex-1 flex-col p-4">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="font-medium">{app.title}</h2>
                  <span className="shrink-0 rounded-full border border-line px-2 py-0.5 text-xs text-muted">
                    {app.projectType}
                  </span>
                </div>
                <p className="mt-1 line-clamp-2 flex-1 text-sm text-muted">
                  {app.description ?? 'No description.'}
                </p>
                <p className="mt-2 text-xs text-muted">
                  by {app.creator}
                  {app.rating !== null && ` · ${app.rating}/5 (${app.ratingCount})`}
                  {app.remixCount > 0 && ` · ${app.remixCount} remix${app.remixCount === 1 ? '' : 'es'}`}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  {app.playUrl && (
                    <a
                      href={app.playUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg bg-accent px-2.5 py-1 text-xs font-medium text-accent-ink transition-opacity hover:opacity-90"
                    >
                      Play
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => void remix(app)}
                    disabled={remixing === app.id}
                    className="rounded-lg border border-line px-2.5 py-1 text-xs transition-colors hover:bg-page disabled:opacity-50"
                  >
                    {remixing === app.id ? 'Copying' : 'Remix'}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
